import { useMemo, useState } from 'react';
import { Plus, Trash2, ExternalLink, Pencil } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/tables/DataTable';
import { EditSheet } from '@/components/layout/EditSheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  useAdminContents,
  useUpsertContent,
  useDeleteContent,
  useAdminInfluencers,
  EMPTY_CONTENT,
  type ContentRow,
} from '@/lib/admin-queries';
import { ContentEditForm } from '@/features/contents/ContentEditForm';
import { toast } from '@/components/ui/Toast';

const CATEGORY_LABEL: Record<ContentRow['category'], string> = {
  'short-drama': 'Short drama',
  'micro-series': 'Micro-series',
  promo: 'Promo',
  'behind-the-scenes': 'Behind the scenes',
};

export function ContentsPage() {
  const { data: rows = [], isLoading } = useAdminContents();
  const { data: influencers = [] } = useAdminInfluencers();
  const upsert = useUpsertContent();
  const remove = useDeleteContent();
  const [editing, setEditing] = useState<ContentRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ContentRow | null>(null);

  const influencerById = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of influencers) {
      if (i.id) m.set(i.id, i.name);
    }
    return m;
  }, [influencers]);

  const columns = useMemo(
    () => [
      {
        key: 'title',
        header: 'Title',
        cell: (r: ContentRow) => (
          <div>
            <p className="font-medium">{r.title}</p>
            <p className="text-xs text-fg-subtle">{r.slug}</p>
          </div>
        ),
      },
      {
        key: 'category',
        header: 'Category',
        cell: (r: ContentRow) => <Badge variant="gold">{CATEGORY_LABEL[r.category]}</Badge>,
      },
      {
        key: 'influencer',
        header: 'Influencer',
        cell: (r: ContentRow) => (
          <span className="text-sm text-fg-muted">
            {r.primary_influencer_id ? influencerById.get(r.primary_influencer_id) ?? '—' : '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        cell: (r: ContentRow) => (
          <Badge variant={r.status === 'published' ? 'gold' : 'default'}>{r.status}</Badge>
        ),
      },
      {
        key: 'featured',
        header: 'Featured',
        cell: (r: ContentRow) => (r.is_featured ? <Badge variant="gold">Featured</Badge> : <span className="text-fg-subtle">—</span>),
      },
      {
        key: 'actions',
        header: '',
        align: 'right' as const,
        cell: (r: ContentRow) => (
          <div className="flex items-center justify-end gap-1">
            {r.status === 'published' && (
              <a
                href={`https://vonlof.co/contents/${r.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View live"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <button
              type="button"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-danger/15 hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [influencerById],
  );

  async function onSave(row: ContentRow) {
    try {
      await upsert.mutateAsync(row);
      toast.success(row.id ? 'Drama updated' : 'Drama created');
      setEditing(null);
      setCreating(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onConfirmDelete() {
    if (!deleting?.id) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success('Drama deleted');
      setDeleting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Content Creation</p>
          <h1 className="mt-2 display-serif text-display-l text-fg">Short dramas</h1>
          <p className="mt-2 text-sm text-fg-muted">Series promoted by the Content Creation department. Edit cover, summary, social links, and the primary influencer.</p>
        </div>
        <Button
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => {
            setEditing({ ...EMPTY_CONTENT });
            setCreating(true);
          }}
        >
          New drama
        </Button>
      </header>

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id ?? r.slug}
          onRowClick={(r) => setEditing(r)}
          empty="No dramas yet. Click New drama to add one."
        />
      )}

      <EditSheet
        open={editing !== null}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={creating ? 'New drama' : 'Edit drama'}
        description="Changes save on submit."
        size="xl"
      >
        {editing && (
          <ContentEditForm
            value={editing}
            submitting={upsert.isPending}
            onSubmit={onSave}
            onCancel={() => {
              setEditing(null);
              setCreating(false);
            }}
          />
        )}
      </EditSheet>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this drama?"
        description={
          deleting
            ? `"${deleting.title}" will be removed from the marketing site. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete drama"
        destructive
        loading={remove.isPending}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}
